import { useEffect, useState } from "react";
import { X, List, ChevronRight, ArrowUp } from "lucide-react";

export type BlogHeading = { id: string; text: string; level: number };

const TITLE_CASE_LOWER = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "from",
  "in",
  "into",
  "is",
  "it",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "with",
  "over",
  "via",
  "per",
]);

function toTitleCase(text: string): string {
  const words = text.split(/\s+/);
  return words
    .map((w, i) => {
      if (!w) return w;
      // Preserve all-caps acronyms (e.g. NIST, AI, NASA, USDS, DAI)
      // and plural acronyms ending in lowercase 's' (e.g. CBDCs, NFTs, APIs)
      const stripped = w.replace(/[.,;:!?)\]]+$/g, "");
      const trailing = w.slice(stripped.length);
      if (/^[A-Z0-9]{2,}$/.test(stripped)) return w;
      if (/^[A-Z0-9]{2,}s$/.test(stripped)) {
        return stripped.slice(0, -1) + "s" + trailing;
      }
      const lower = w.toLowerCase();
      if (i !== 0 && i !== words.length - 1 && TITLE_CASE_LOWER.has(lower)) {
        return lower;
      }
      // Capitalize the first letter, skipping leading quotes/punctuation
      const m = lower.match(/^([^a-z0-9]*)([a-z0-9])(.*)$/i);
      if (m) return m[1] + m[2].toUpperCase() + m[3];
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function shortenHeading(text: string): string {
  // Strip leading numbering like "1.", "1)", "Step 1:"
  let t = text.replace(/^\s*(?:step\s*)?\d+[\).:\-]\s*/i, "").trim();
  // Prefer the part before a colon or em/en dash (usually the topic)
  const splitMatch = t.split(/\s[—–-]\s|:\s/);
  if (splitMatch[0] && splitMatch[0].length >= 3) t = splitMatch[0];
  // Remove trailing punctuation
  t = t.replace(/[.?!,;:]+$/g, "").trim();
  return toTitleCase(t);
}

export function BlogTOC({
  headings,
  totalReadMinutes,
  title,
}: {
  headings: BlogHeading[];
  totalReadMinutes?: number;
  title?: string;
}) {
  const [open, setOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [progressPct, setProgressPct] = useState(0);

  // Active heading via IntersectionObserver
  useEffect(() => {
    if (headings.length === 0) return;
    const els = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [headings]);

  // Back-to-top visibility
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 400);
      setRevealed(y > 480);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgressPct(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hasTOC = headings.length > 0;
  const minutesLeft =
    totalReadMinutes && totalReadMinutes > 0
      ? Math.max(0, Math.round(totalReadMinutes * (1 - progressPct)))
      : null;

  return (
    <>
      <aside
        aria-label="Table of contents"
        className={`sticky top-24 hidden max-h-[calc(100vh-7rem)] self-start transition-all duration-500 ease-out min-[1151px]:block ${
          revealed ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        {hasTOC && open ? (
          <div className="flex max-h-[calc(100vh-7rem)] flex-col rounded-lg border border-white/10 bg-white/[0.04] p-4 backdrop-blur-md">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                  On this page
                </span>
                {minutesLeft !== null && (
                  <>
                    <span aria-hidden className="h-3 w-px bg-white/20" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                      {minutesLeft === 0 ? "Almost done" : `${minutesLeft} min left`}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Hide table of contents"
                className="rounded-md p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <nav className="scrollbar-subtle overflow-y-auto pr-1">
              <ul className="space-y-1">
                {title ? (
                  <li>
                    <a
                      href="#top"
                      onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        history.replaceState(null, "", window.location.pathname);
                      }}
                      className={`group flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[12.5px] font-medium leading-snug transition-colors ${
                        activeId === null
                          ? "bg-white/10 text-white"
                          : "text-white/80 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <ChevronRight
                        className={`mt-0.5 h-3 w-3 shrink-0 transition-transform ${
                          activeId === null ? "translate-x-0.5 text-white" : "text-white/70"
                        }`}
                      />
                      <span className="line-clamp-2" title={title}>
                        {title}
                      </span>
                    </a>
                  </li>
                ) : null}
                {headings.map((h) => {
                  const isActive = activeId === h.id;
                  return (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(h.id);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                            history.replaceState(null, "", `#${h.id}`);
                          }
                        }}
                        className={`group flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[12.5px] leading-snug transition-colors ${
                          h.level === 2
                            ? "pl-6 text-[12px]"
                            : h.level >= 3
                              ? "pl-10 text-[11.5px] text-white/55"
                              : ""
                        } ${
                          isActive
                            ? "bg-white/10 text-white"
                            : "text-white/65 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <ChevronRight
                          className={`mt-0.5 h-3 w-3 shrink-0 transition-transform ${
                            isActive ? "translate-x-0.5 text-white" : "text-white/70"
                          }`}
                        />
                        <span className="line-clamp-2" title={h.text}>
                          {shortenHeading(h.text)}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        ) : hasTOC ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Show table of contents"
            className="flex items-center gap-2 rounded-md border border-white/15 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
          >
            <List className="h-3.5 w-3.5" />
            Contents
          </button>
        ) : null}
      </aside>

      {/* Back to top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:bg-white/20 hover:scale-105 ${
          scrolled ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </>
  );
}
